import type { Guest } from "@/types/Guest";

/**
 * Per-variable SOURCE support for email templates (preview side).
 *
 * A `{{mainGuest.<field>}}` placeholder resolves against the recipient's
 * MAIN GUEST instead of the recipient. Companions are full Guest records
 * linked to their main guest via `mainGuest` (ObjectId) and carry it,
 * pre-joined, as `mainGuestDTO`. To make the dot-path `{{mainGuest.firstName}}`
 * resolve through {@link getNestedValue}, the substitution context must hold
 * the main-guest OBJECT under the `mainGuest` key (the raw `mainGuest` field is
 * just the id string, which a nested walk can't descend into).
 *
 * Mirrors the backend send-time enrichment
 * (`main-consumer/src/lib/context-enricher.ts` `enrichMainGuest`) so preview ==
 * real send.
 *
 * EMPTY-SAFE (hard requirement): the template author never knows at build time
 * whether a mail goes to a main guest or a companion. When the recipient has no
 * main guest the `mainGuest` context key is set to `undefined`, so every
 * `{{mainGuest.*}}` token resolves to "" — blank, never a literal `{{...}}`.
 *
 * CREDENTIAL LANDMINE: `username` / `code` are login credentials and are
 * stripped from the injected object (parity with the companion data-table
 * catalog), so the main-guest source can never leak a sibling's login even if
 * a placeholder were hand-typed.
 */

/**
 * Fields exposed through the `{{mainGuest.<field>}}` source. EXACT parity with
 * the backend whitelist (`MAIN_GUEST_SOURCE_FIELDS` in
 * `main-consumer/src/lib/context-enricher.ts`) and the FE registry
 * (`MAIN_GUEST_VARS`), so the set of resolvable `mainGuest.*` keys is identical
 * in preview and at real send — no field renders in preview but blanks on send.
 *
 * Whitelist (not blacklist) on purpose: `username` / `code` (login creds) are
 * excluded, and so is everything else not on the list (`guestType`,
 * `numCompanions`, relations, …) — the renderer must not expose the main
 * guest's full record, only these safe profile fields + the event custom
 * fields (`additionalFields`, same as already exposed for the recipient).
 */
const MAIN_GUEST_SOURCE_FIELDS: ReadonlyArray<keyof Guest> = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "mobile",
  "company",
  "jobTitle",
  "title",
  "gender",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  // Event custom fields, resolvable as `{{mainGuest.additionalFields.<name>}}`.
  "additionalFields",
];

/**
 * Field projection for fetching a main guest by id (role-scoped via the normal
 * guest API, so the viewer's field-scope masking applies). Derived from the
 * whitelist so it can't drift, plus `guestId` (to map fetched rows back to
 * their companion) and `guestType`. Deliberately NO `username` / `code`.
 */
export const MAIN_GUEST_FETCH_FIELDS = [
  "guestId",
  "guestType",
  ...MAIN_GUEST_SOURCE_FIELDS,
].join(",");

/** Project a main-guest DTO down to the safe whitelisted fields before it
 *  enters the substitution context. */
export function sanitizeMainGuestSource(
  dto: Guest | null | undefined,
): Record<string, unknown> | undefined {
  if (!dto || typeof dto !== "object") return undefined;
  const src = dto as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const f of MAIN_GUEST_SOURCE_FIELDS) {
    const v = src[f as string];
    if (v !== undefined && v !== null) out[f as string] = v;
  }
  return out;
}

/**
 * Return a substitution context for `guest` with the `mainGuest` key bound to
 * the sanitized main-guest object (or `undefined` when there is no main guest).
 *
 * The result is a shallow copy; the source guest is never mutated. The
 * `mainGuest` key is intentionally overridden from the raw id string to the
 * object so dot-path tokens resolve — preview-only, never persisted.
 */
export function withMainGuestVariables<T extends Guest>(
  guest: T,
): T {
  const source = sanitizeMainGuestSource(guest.mainGuestDTO);
  // Cast: `mainGuest` is typed as the id string on Guest, but in the
  // preview substitution context it carries the resolved object.
  return { ...guest, mainGuest: source } as unknown as T;
}
